/**
 * impersonation.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The browser half of "Sign in as vendor" (server: authController
 * startVendorImpersonation). The dashboard keeps exactly one session in
 * localStorage (`profile`, `token`, `refreshToken`), so a vendor session has
 * to REPLACE the admin's while it runs. This module is the only place that
 * swap happens, in both directions:
 *
 *   begin()  stash the admin's three keys under ADMIN_STASH_KEY, write the
 *            vendor's session in their place exactly the way the AUTH reducer
 *            writes a login, and record the banner's facts under META_KEY.
 *   end()    put the admin's keys back and clear both extras.
 *
 * The in-house order cart travels with the session (4 Oct 2026). It lives in
 * localStorage too (`cartItems`, `shippingAddress`, `paymentMethod`), so an
 * admin's half-built order for one vendor's products was still in the cart
 * after signing in as a different vendor, and anything the vendor session
 * added was still there when the admin came back. begin() now stashes the
 * admin's cart with their session and starts the vendor on an empty one; end()
 * puts the admin's cart back and drops the vendor's.
 *
 * Everything reads through `active()`, so a page that wants to know whether
 * it is looking at a vendor's dashboard on the admin's behalf asks here.
 */
import { encryptData } from "./encryption.js";

export const META_KEY = "lusuku.impersonation";
const ADMIN_STASH_KEY = "lusuku.impersonation.adminSession";
const SESSION_KEYS = ["profile", "token", "refreshToken"];
const CART_KEYS = ["cartItems", "shippingAddress", "paymentMethod"];
// Everything that belongs to the signed-in person and is swapped with them.
const STASHED_KEYS = [...SESSION_KEYS, ...CART_KEYS];

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_err) {
    return null;
  }
};
const write = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (_err) {
    /* private mode or full quota: the caller decides what that means */
  }
};

/** The active impersonation, or null. Expired metadata counts as none. */
export const active = () => {
  const raw = read(META_KEY);
  if (!raw) return null;
  try {
    const meta = JSON.parse(raw);
    if (meta?.expiresAt && new Date(meta.expiresAt).getTime() < Date.now()) {
      return { ...meta, expired: true };
    }
    return meta;
  } catch (_err) {
    return null;
  }
};

export const hasAdminStash = () => Boolean(read(ADMIN_STASH_KEY));

/**
 * The ADMIN's own access token while a vendor session is in place, or null.
 * For the few super-admin calls that exist for exactly this situation (Set
 * login on a vendor's team member from their My Team page): the request opts
 * in with `adminSession: true` (api/index.js) and goes out as the admin, so
 * the server checks the admin's role and the audit names the admin. It is the
 * admin acting as themselves, not a vendor session borrowing admin power.
 */
export const adminSessionToken = () => {
  const raw = read(ADMIN_STASH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw)?.token || null;
  } catch (_err) {
    return null;
  }
};

/**
 * Swap the admin's session out for the vendor's.
 * @param {{ result: object, token: string, impersonation: object }} payload
 *   exactly what POST /user/impersonate/restaurant/:id returns
 */
export const begin = (payload) => {
  if (!payload?.token || !payload?.result) {
    throw new Error("Impersonation payload is missing the session");
  }
  // Never stack: a second vendor over a stashed admin would lose the admin.
  if (!hasAdminStash()) {
    const stash = {};
    STASHED_KEYS.forEach((key) => {
      stash[key] = read(key);
    });
    write(ADMIN_STASH_KEY, JSON.stringify(stash));
  }
  // The vendor starts on an empty cart, never on the admin's (or a previous
  // vendor's, when one session replaces another).
  CART_KEYS.forEach((key) => write(key, null));
  // The same three writes reducers/auth.js makes on AUTH, so every reader of
  // the session (getUserFromLocalStorage, the API interceptor) sees a login.
  write("profile", encryptData({ result: payload.result, token: payload.token }));
  write("token", payload.token);
  write("refreshToken", null);
  write(META_KEY, JSON.stringify(payload.impersonation || {}));
};

/**
 * Put the admin back. Returns true when there was an admin to restore.
 * Safe to call when nothing is active (a no-op that clears leftovers).
 */
export const end = () => {
  const raw = read(ADMIN_STASH_KEY);
  write(META_KEY, null);
  write(ADMIN_STASH_KEY, null);
  if (!raw) return false;
  try {
    const stash = JSON.parse(raw);
    // A key the stash lacks is cleared, not kept: what is there now is the
    // vendor's, and it must not follow the admin back.
    STASHED_KEYS.forEach((key) => write(key, stash[key] ?? null));
    return Boolean(stash.token);
  } catch (_err) {
    return false;
  }
};

/**
 * Forget the impersonation WITHOUT restoring anything. For a plain sign-out
 * while a vendor session is active: the admin chose to leave, so nothing is
 * put back, but the stash and the banner's facts must not survive into the
 * next login as a phantom "Viewing as".
 */
export const clear = () => {
  write(META_KEY, null);
  write(ADMIN_STASH_KEY, null);
};

/** Where the admin lands after returning: the directory they came from. */
export const RETURN_ROUTE = "/admin/restaurants";
